<template>
  <div>
    <Dialog
    :stateId="dialogId"
    maxWidth="1000px"
    @dialog-closing="close"
    @close="close">
      <v-card v-if="show" id="bid-manager">
        <v-card-title class="primary white--text">
          <span>{{ $t('bids') }}</span>
          <v-spacer/>
          <Icon
          :small="false"
          :large="true"
          :icon="IconHelpers.getIconForObjectType('myTracts', myTractsOnly)"
          iconColor="white"
          margin="mr-2"
          :tooltipText="myTractsOnly ? $t('allTracts') : $t('myTracts')"
          elementId="bid-my-tracts-toggle"
          @icon-clicked="myTractsOnly = !myTractsOnly"/>
          <div class="bid-manager-selectors mr-3">
            <DestinationSelector
            :contractMode="ContractMode.Logs"
            immediate
            hideInternalDestinations
            :solo="false"
            outlined
            rounded
            dense
            dark
            color="white"
            :prependInnerIcon="IconHelpers.getIconForObjectType('destination')"
            style="min-width: 260px"
            @item-selected="onDestinationSelected"/>
          </div>
          <BaseDialogActions
          hideRefresh
          customCancel
          :dialogId="dialogId"
          elementId="bid-manager-actions"
          @close="close"/>
        </v-card-title>
        <v-card-text class="pt-2">
          <v-row no-gutters align="end">
            <v-col>
              <v-tabs v-model="tab" class="card-tabs sub-tabs" id="bid-manager-tabs">
                <v-tab id="bid-reporting-tab">
                  <v-icon class="mr-1" small>mdi-chart-box-outline</v-icon>
                  {{ $t('reporting') }}
                </v-tab>
                <v-tab v-if="inboxEnabled" id="bid-inbox-tab">
                  <v-icon class="mr-1" small>mdi-inbox</v-icon>
                  {{ $t('inbox') }}
                </v-tab>
              </v-tabs>
            </v-col>
            <v-col cols="auto" class="bid-manager-selectors">
              <TimeframeSelector
              :timeframes="reportingTimeframes"
              :max="366"
              storeSelection
              @item-selected="onTimeframeSelected"/>
            </v-col>
          </v-row>
          <v-card outlined style="border-radius: 0px 4px 4px 4px">
            <v-window v-model="tab" touchless>
            <v-window-item>
              <v-card-text>
                <BidActivityChart
                v-if="selectionReady"
                :statisticsParams="statisticsParams"
                @open-tracts="openTractStatsDialog"
                @open-suppliers="openSupplierStatsDialog"/>
              </v-card-text>
            </v-window-item>
            <v-window-item v-if="inboxEnabled">
              <ShortcutWrapper isDialog @n-shortcut="onCreateShortcut">
              <DataTable
          :items="bidsForDataTable"
          :headers="headers"
          :actions="tableActions"
          :loading.sync="loading"
          :customCells="customColumns"
          htmlIdentifier="bid-manager-table"
          defaultSort="bidDate"
          sortDesc
          showPointerCursor
          @refresh="refreshBids"
          @create="openCreate"
          @click:row="openDetail($event.data)">
            <template #custom-controls>
              <v-col cols="auto" class="d-flex align-center">
                <BidFilter :filter="filters" @filter-applied="onFilterApplied"/>
              </v-col>
            </template>
            <template #bidDate="{item}">
              <span>{{ formatDate(item.bidDate) }}</span>
            </template>
            <template #bidExpiration="{item}">
              <span>{{ formatDate(item.bidExpiration) }}</span>
              <Icon
              v-if="isBidExpired(item)"
              icon="mdi-timer-alert-outline"
              iconColor="error"
              :tooltipText="$t('bidExpired')"/>
            </template>
            <template #status="{item}">
              <Icon
              v-if="isNewBid(item)"
              icon="mdi-creation"
              iconColor="info"
              margin=""
              :tooltipText="$t('newBid')"/>
            </template>
            <template #actions="{item}">
              <Icon
              icon="mdi-pencil"
              iconColor="success"
              elementId="edit-bid"
              :tooltipText="$t('edit')"
              @icon-clicked="openEdit(item)"/>
              <Icon
              v-if="!item.tractId"
              icon="mdi-crosshairs-gps"
              iconColor="info"
              elementId="associate-to-tract"
              :tooltipText="$t('associateToTract')"
              @icon-clicked="openAssociate(item)"/>
              <Icon
              icon="mdi-delete-forever"
              iconColor="error"
              elementId="delete-bid"
              :tooltipText="$t('delete')"
              @icon-clicked="promptDelete(item)"/>
            </template>
          </DataTable>
              </ShortcutWrapper>
            </v-window-item>
            </v-window>
          </v-card>
        </v-card-text>
      </v-card>
    </Dialog>

    <BidWizard ref="bidWizard" @saved="onWizardSaved"/>
    <BidDetailDialog ref="bidDetail" @edit="onDetailEdit"/>

    <Dialog :stateId="promoteDialogId" maxWidth="90vw" @dialog-closing="closePromote" @close="closePromote">
      <TractForm
      v-if="promoting"
      :propSeedTract="promoteSeed"
      :tractTypeFilter="deliveredTractTypesOnly"
      @tract-created="onPromoteTractCreated"
      @close="closePromote"/>
    </Dialog>

    <v-dialog v-model="associating" width="500" v-if="associating">
      <v-card id="associate-bid-to-tract">
        <v-card-title class="primary white--text">
          <span>{{ $t('associateToTract') }}</span>
          <v-spacer/>
          <BaseDialogActions
          hideRefresh
          hideFullscreen
          customCancel
          elementId="associate-bid-actions"
          @close="closeAssociate"/>
        </v-card-title>
        <v-card-text class="pt-4">
          <FormWrapper
          formRef="associateForm"
          :buttonText="$t('confirm')"
          :disabled="!associateTract || associateLoading"
          @submit="confirmAssociate">
            <v-row dense align="center">
              <v-col>
                <BidTractAutocomplete
                clearable
                :tractFilter="isDeliveredTract"
                :originSpot="associateBid ? associateBid.spot : null"
                @tract-chosen="associateTract = $event"
                @tract-cleared="associateTract = undefined"/>
              </v-col>
              <v-col cols="auto">
                <Icon
                icon="mdi-plus"
                iconColor="primary"
                :small="false"
                tabindex="0"
                elementId="associate-create-tract"
                :tooltipText="$t('createNewTract')"
                @icon-clicked="openCreateTract"/>
              </v-col>
            </v-row>
            <template #left-action>
              <v-btn text @click="closeAssociate">{{ $t('cancel') }}</v-btn>
            </template>
          </FormWrapper>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="tractStatsDialog" width="800" v-if="tractStatsDialog">
      <v-card id="bid-tract-stats">
        <v-card-title class="primary white--text">
          <span><v-icon class="mr-1" color="white">{{ IconHelpers.getIconForObjectType('tract') }}</v-icon>{{ $t('tracts') }}</span>
          <v-spacer/>
          <BaseDialogActions
          hideRefresh
          hideFullscreen
          customCancel
          elementId="bid-tract-stats-actions"
          @close="tractStatsDialog = false"/>
        </v-card-title>
        <v-card-text class="pt-4">
          <DataTable
          :items="tractStats"
          :headers="tractStatHeaders"
          :loading="tractStatsLoading"
          :customCells="tractStatCustomColumns"
          :propItemsPerPage="15"
          htmlIdentifier="bid-tract-stats-table"
          defaultSort="tractName"
          showPointerCursor
          @click:row="onTractStatRowClick($event.data)">
            <template #state="{item}">
              <v-chip small :color="tractBidStateColor(item.state)" text-color="white">
                {{ tractBidStateLabel(item.state) }}
              </v-chip>
            </template>
            <template #decidedDate="{item}">
              <span>{{ item.decidedDate ? formatDate(item.decidedDate) : '—' }}</span>
            </template>
          </DataTable>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="supplierStatsDialog" width="800" v-if="supplierStatsDialog">
      <v-card id="bid-supplier-stats">
        <v-card-title class="primary white--text">
          <span><v-icon class="mr-1" color="white">{{ IconHelpers.getIconForObjectType('account') }}</v-icon>{{ $t('suppliers') }}</span>
          <v-spacer/>
          <BaseDialogActions
          hideRefresh
          hideFullscreen
          customCancel
          elementId="bid-supplier-stats-actions"
          @close="supplierStatsDialog = false"/>
        </v-card-title>
        <v-card-text class="pt-4">
          <DataTable
          :items="supplierStatsForTable"
          :headers="supplierStatHeaders"
          :loading="supplierStatsLoading"
          :customCells="supplierStatCustomColumns"
          :propItemsPerPage="15"
          htmlIdentifier="bid-supplier-stats-table"
          defaultSort="supplierName">
            <template #winRate="{item}">
              <span>{{ item.winRate !== null ? `${item.winRate}%` : '—' }}</span>
            </template>
          </DataTable>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleting" width="400" v-if="deleting">
      <ConfirmDelete
      :title="$t('bid')"
      @delete="confirmDelete"
      @cancel-delete="cancelDelete"/>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import BidHeaders from '@/headers/Bid.js'
import IconHelpers from '@/utils/IconHelpers'
import { ContractMode, RfbStatus, TractBidState, TractTypeCategory } from '@/utils/Enumerations.js'
import { isDeliveredTract } from '@/model/Tract.js'
import { timeframesInLocalTime, timeframeToUtcIsoString, utcToLocalDate } from '@/utils/DateFormatter.js'
import { uniqueDialogId } from '@/utils/componentHelpers'

// TODO: IH - restore after tractless bids are injected into environments from comms portal
const inboxEnabled = false

export default {
  name: 'BidManager',

  components: {
    BidActivityChart: () => import('@/components/bid/BidActivityChart.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    BidFilter: () => import('@/components/tract/BidFilter.vue'),
    BidTractAutocomplete: () => import('@/components/bid/BidTractAutocomplete.vue'),
    BidWizard: () => import('@/components/tract/BidWizard.vue'),
    BidDetailDialog: () => import('@/components/bid/BidDetailDialog.vue'),
    TractForm: () => import('@/components/tract/tract-form/TractForm.vue'),
    DestinationSelector: () => import('@/components/dashboard/DestinationSelector.vue'),
    TimeframeSelector: () => import('@/components/dashboard/TimeframeSelector.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('bid-manager-dialog'),
    inboxEnabled,
    ContractMode,
    IconHelpers,
    tab: 0,
    show: false,
    destinationReady: false,
    timeframeReady: false,
    selection: {
      sinceTime: undefined,
      untilTime: undefined,
      destinationAccountId: undefined
    },
    myTractsOnly: false,
    tractStats: [],
    tractStatsLoading: false,
    tractStatsRequestId: 0,
    tractStatsKey: undefined,
    tractStatsDialog: false,
    supplierStats: [],
    supplierStatsLoading: false,
    supplierStatsRequestId: 0,
    supplierStatsKey: undefined,
    supplierStatsDialog: false,
    bids: [],
    loading: false,
    filters: {
      tractId: undefined,
      supplierAccountId: undefined,
      destinationAccountId: undefined,
      unassignedOnly: false
    },
    associating: false,
    associateBid: undefined,
    associateRequestId: 0,
    associateTract: undefined,
    associateLoading: false,
    promoteDialogId: uniqueDialogId('bid-promote-dialog'),
    promoting: false,
    promoteBid: undefined,
    deleting: false,
    deleteTarget: undefined
  }),

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tract', ['allTracts']),
    ...mapGetters('locations', ['allLocations']),

    selectionReady () {
      return this.destinationReady && this.timeframeReady
    },

    reportingTimeframes () {
      const tf = timeframesInLocalTime
      return [tf.last7Days, tf.last30Days, tf.last3Months, tf.last6Months, tf.last12Months].map(timeframeToUtcIsoString)
    },

    statisticsParams () {
      const params = {}
      const destinationId = this.selection.destinationAccountId
      if (destinationId != null && destinationId !== -1) params.destinationAccountId = destinationId
      if (this.selection.sinceTime) params.bidDateFrom = this.selection.sinceTime
      if (this.selection.untilTime) params.bidDateTo = this.selection.untilTime
      if (this.myTractsOnly) params.myTractsOnly = true
      return params
    },

    tractStatHeaders () {
      return BidHeaders.bidTractStatHeaders()
    },

    tractStatCustomColumns () {
      return [
        { slotName: 'state', value: 'state' },
        { slotName: 'decidedDate', value: 'decidedDate' }
      ]
    },

    supplierStatHeaders () {
      return BidHeaders.bidSupplierStatHeaders()
    },

    supplierStatCustomColumns () {
      return [
        { slotName: 'winRate', value: 'winRate' }
      ]
    },

    supplierStatsForTable () {
      return this.supplierStats.map(s => {
        const decided = s.won + s.lost
        return { ...s, winRate: decided > 0 ? Math.round((s.won / decided) * 100) : null }
      })
    },

    headers () {
      return BidHeaders.bidManagerHeaders(this.hasNewBids)
    },

    hasNewBids () {
      return this.bids.some(b => this.isNewBid(b))
    },

    customColumns () {
      return [
        { slotName: 'status', value: 'status' },
        { slotName: 'bidDate', value: 'bidDate' },
        { slotName: 'bidExpiration', value: 'bidExpiration' },
        { slotName: 'actions', value: 'actions' }
      ]
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh')
        },
        {
          actionName: 'create',
          icon: 'mdi-plus',
          text: this.$t('createBid'),
          id: 'create-bid'
        }
      ]
    },

    bidsForDataTable () {
      return this.bids.map(bid => ({
        ...bid,
        supplierAccountName: this.accountName(bid.supplierAccountId),
        destinationAccountName: this.accountName(bid.destinationAccountId),
        tractName: this.tractName(bid.tractId)
      }))
    },

    nestedDialogOpen () {
      return this.associating || this.deleting || this.tractStatsDialog || this.supplierStatsDialog
    },

    promoteSeed () {
      const bid = this.promoteBid
      if (!bid) return null
      return {
        name: bid.name,
        spot: bid.spot
          ? {
            name: bid.name,
            latitude: bid.spot.latitude,
            longitude: bid.spot.longitude,
            countrySubdivision: bid.spot.countrySubdivision,
            countrySecondarySubdivision: bid.spot.countrySecondarySubdivision
          }
          : null,
        harvestStartDate: bid.harvestStartDate,
        harvestEndDate: bid.harvestEndDate,
        supplierAccountId: bid.supplierAccountId,
        contractAccountId: bid.supplierAccountId,
        timberCategoryId: bid.timberCategoryId,
        conversionId: bid.conversionId,
        landownerTypeId: bid.landownerTypeId
      }
    }
  },

  watch: {
    nestedDialogOpen (val) {
      if (val) {
        this.setAllowEscapeToClose(false)
      } else {
        setTimeout(() => { if (!this.nestedDialogOpen) this.setAllowEscapeToClose(true) }, 0)
      }
    }
  },

  methods: {
    ...mapActions('bid', ['fetchBids', 'fetchBid', 'updateBid', 'deleteBid', 'fetchBidTractStatistics', 'fetchBidSupplierStatistics']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('tract', ['fetchTracts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapMutations('dialog', ['setAllowEscapeToClose']),
    ...mapActions('locations', ['fetchLocations']),

    tractBidStateLabel: (x) => TractBidState.fromInt(x),

    tractBidStateColor (state) {
      switch (state) {
        case TractBidState.Won.value: return 'success'
        case TractBidState.Lost.value: return 'error'
        default: return 'info'
      }
    },

    isDeliveredTract,

    deliveredTractTypesOnly (tractType) {
      return tractType.category === TractTypeCategory.Delivered.value
    },

    isNewBid (bid) {
      return !bid.tractId && (bid.outcome === null || bid.outcome === undefined)
    },

    isBidOpen (bid) {
      return (bid.outcome === null || bid.outcome === undefined) &&
        bid.rfbStatus !== RfbStatus.Withdrawn.value &&
        bid.rfbStatus !== RfbStatus.Pass.value
    },

    isBidExpired (bid) {
      return this.isBidOpen(bid) && !!bid.bidExpiration && new Date(bid.bidExpiration) < new Date()
    },

    formatDate (date) {
      return date ? utcToLocalDate(date) : this.$t('notAvailable')
    },

    accountName (accountId) {
      if (accountId === null || accountId === undefined) return this.$t('notAvailable')
      return this.allAccounts.find(a => a.accountId === accountId)?.name ?? accountId
    },

    tractName (tractId) {
      if (tractId === null || tractId === undefined) return this.$t('unassigned')
      return this.allTracts.find(t => t.tractId === tractId)?.name ?? tractId
    },

    open () {
      this.show = true
      this.tab = 0
      this.destinationReady = false
      this.timeframeReady = false
      this.tractStatsKey = undefined
      this.supplierStatsKey = undefined
      this.openOrUpdateDialog({ id: this.dialogId, width: '1000px', allowFullscreen: true })
      if (!this.allAccounts.length) this.fetchAccounts()
      if (!this.allLocations.length) this.fetchLocations()
      if (inboxEnabled) {
        if (!this.allTracts.length) this.fetchTracts()
        this.refreshBids()
      }
    },

    onDestinationSelected (dest) {
      this.selection = {
        ...this.selection,
        destinationAccountId: dest.destinationAccountId ?? dest.location?.accountId
      }
      this.destinationReady = true
    },

    onTimeframeSelected (timePeriod) {
      this.selection = {
        ...this.selection,
        sinceTime: timePeriod.sinceTime,
        untilTime: timePeriod.untilTime
      }
      this.timeframeReady = true
    },

    openTractStatsDialog () {
      this.tractStatsDialog = true
      this.loadTractStats()
    },

    onTractStatRowClick (row) {
      if (!row?.tractId) return
      this.$emit('tract-detail', row)
    },

    openSupplierStatsDialog () {
      this.supplierStatsDialog = true
      this.loadSupplierStats()
    },

    async loadTractStats () {
      const key = JSON.stringify(this.statisticsParams)
      if (key === this.tractStatsKey) return
      const requestId = ++this.tractStatsRequestId
      this.tractStatsLoading = true
      try {
        const stats = await this.fetchBidTractStatistics(this.statisticsParams)
        if (requestId === this.tractStatsRequestId) {
          this.tractStats = stats
          this.tractStatsKey = key
        }
      } finally {
        if (requestId === this.tractStatsRequestId) this.tractStatsLoading = false
      }
    },

    async loadSupplierStats () {
      const key = JSON.stringify(this.statisticsParams)
      if (key === this.supplierStatsKey) return
      const requestId = ++this.supplierStatsRequestId
      this.supplierStatsLoading = true
      try {
        const stats = await this.fetchBidSupplierStatistics(this.statisticsParams)
        if (requestId === this.supplierStatsRequestId) {
          this.supplierStats = stats
          this.supplierStatsKey = key
        }
      } finally {
        if (requestId === this.supplierStatsRequestId) this.supplierStatsLoading = false
      }
    },

    close () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.tractStatsDialog = false
      this.supplierStatsDialog = false
      this.show = false
    },

    onFilterApplied (filter) {
      this.filters = filter
      this.refreshBids()
    },

    async refreshBids () {
      this.loading = true
      try {
        this.bids = await this.fetchBids({
          tractId: this.filters.tractId,
          unassignedOnly: this.filters.unassignedOnly ? true : undefined,
          supplierAccountId: this.filters.supplierAccountId,
          destinationAccountId: this.filters.destinationAccountId,
          rfbStatus: RfbStatus.Submitted.value
        })
      } finally {
        this.loading = false
      }
    },

    onCreateShortcut () {
      const wizardOpen = this.$refs.bidWizard && this.$refs.bidWizard.show
      if (wizardOpen || this.associating || this.promoting || this.deleting) return
      this.openCreate()
    },

    openCreate () {
      this.$refs.bidWizard.open({})
    },

    openEdit (bid) {
      this.$refs.bidWizard.open({ bid })
    },

    openDetail (bid) {
      this.$refs.bidDetail.open(bid)
    },

    onWizardSaved () {
      this.refreshBids()
      this.$refs.bidDetail.refresh()
    },

    onDetailEdit (bid) {
      setTimeout(() => this.openEdit(bid), 0)
    },

    async openAssociate (bid) {
      const requestId = ++this.associateRequestId
      this.associateBid = bid
      this.associateTract = undefined
      this.associating = true
      const full = await this.fetchBid(bid.bidId)
      if (requestId === this.associateRequestId && this.associating) this.associateBid = full
    },

    closeAssociate () {
      this.associating = false
      this.associateBid = undefined
      this.associateTract = undefined
    },

    fullBidPayload (bid) {
      return {
        tractId: bid.tractId,
        name: bid.name,
        spot: bid.spot,
        destinationAccountId: bid.destinationAccountId,
        distance: bid.distance,
        supplierAccountId: bid.supplierAccountId,
        bidDate: bid.bidDate,
        bidExpiration: bid.bidExpiration,
        harvestStartDate: bid.harvestStartDate,
        harvestEndDate: bid.harvestEndDate,
        timberCategoryId: bid.timberCategoryId,
        conversionId: bid.conversionId,
        landownerTypeId: bid.landownerTypeId,
        rfbStatus: bid.rfbStatus,
        outcome: bid.outcome,
        reason: bid.reason
      }
    },

    async confirmAssociate () {
      if (!this.associateTract) return
      this.associateLoading = true
      try {
        const full = await this.fetchBid(this.associateBid.bidId)
        await this.updateBid({
          bidId: full.bidId,
          payload: { ...this.fullBidPayload(full), tractId: this.associateTract.tractId }
        })
        this.closeAssociate()
        this.refreshBids()
      } finally {
        this.associateLoading = false
      }
    },

    async openCreateTract () {
      const bid = this.associateBid
      this.closeAssociate()
      this.promoteBid = await this.fetchBid(bid.bidId)
      this.promoting = true
      this.openOrUpdateDialog({ id: this.promoteDialogId, width: '90vw' })
    },

    closePromote () {
      this.closeDialogsAtOrAbove(this.promoteDialogId)
      this.promoting = false
      this.promoteBid = undefined
    },

    async onPromoteTractCreated (tractId) {
      const bid = this.promoteBid
      try {
        await this.updateBid({
          bidId: bid.bidId,
          payload: { ...this.fullBidPayload(bid), tractId }
        })
        this.setSnack(this.$t('bidPromotedToTract'))
      } finally {
        this.closePromote()
        this.fetchTracts()
        this.refreshBids()
      }
    },

    promptDelete (bid) {
      this.deleteTarget = bid
      this.deleting = true
    },

    cancelDelete () {
      this.deleting = false
      this.deleteTarget = undefined
    },

    async confirmDelete () {
      await this.deleteBid(this.deleteTarget.bidId)
      this.cancelDelete()
      this.refreshBids()
    }
  }
}
</script>

<style scoped>
.bid-manager-selectors ::v-deep .v-text-field__details {
  display: none;
}
</style>
